import axios from 'axios';
import { apiEndpoint } from '../../../constants/appConstant';

export interface CaptionData {
  id: string;
  fileName: string;
  language: string;
}

export interface CaptionApiResponse {
  success: boolean;
  message?: string;
  captions?: CaptionData[];
}

export const checkCaptionForUniqueId = async (referenceId: string): Promise<CaptionApiResponse> => {
  try {
    const config = {
      method: 'post',
      url: `${apiEndpoint}/checkCaptionforUniqueId`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        referenceId: referenceId
      }),
    };

    const response = await axios(config);

    // Check if response indicates success and has captions
    if (response.data && response.status === 200) {
      // Check if the response indicates reference ID not found
      if (response.data.message === "ReferenceId data is not available") {
        const errorDetails = response.data.data?.APIResponse?.ErrorDetails || 'No assets have been found for the given reference ID';
        return {
          success: false,
          message: errorDetails
        };
      }
      
      // Check for the actual API response structure with captions
      if (response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        // Map the API response to our expected format
        const captions: CaptionData[] = response.data.data.map((item: any, index: number) => ({
          id: `caption_${index + 1}`, // Generate ID since API doesn't provide one
          fileName: item['Document.OriginalFileName'] || `caption-${item.lang}.${item.type === 'text/vtt' ? 'vtt' : 'srt'}`,
          language: getLanguageFromCode(item.lang) || 'Unknown'
        }));

        return {
          success: true,
          captions: captions
        };
      } else {
        // No captions found for this reference ID
        return {
          success: false,
          message: 'No captions were available for this reference ID'
        };
      }
    } else {
      return {
        success: false,
        message: response.data?.message || 'Failed to find captions for the provided reference ID'
      };
    }
  } catch (error: any) {
    console.error('Caption API Error:', error);
    
    // Handle specific error responses
    if (error.response?.status === 404) {
      return {
        success: false,
        message: 'No captions were available for this reference ID'
      };
    } else if (error.response?.status >= 400) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to find captions for the provided reference ID. Please check the ID and try again.'
      };
    } else {
      return {
        success: false,
        message: 'Failed to connect to caption service. Please try again later.'
      };
    }
  }
};

// Helper function to convert language codes to full names
const getLanguageFromCode = (langCode: string): string => {
  const languageMap: { [key: string]: string } = {
    'en': 'English',
    'es': 'Spanish',
    'fr': 'French',
    'de': 'German',
    'it': 'Italian',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'ja': 'Japanese',
    'ko': 'Korean',
    'zh': 'Chinese',
    'ar': 'Arabic',
    'hi': 'Hindi'
  };
  
  return languageMap[langCode?.toLowerCase()] || langCode?.toUpperCase() || 'Unknown';
};